import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Check, X, Star, Quote, User, Building, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsPanel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    company: '',
    image: '',
    rating: 5,
    text: '',
    is_active: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await fetch(`${API_URL}/api/testimonials?active_only=false`);
      if (res.ok) {
        const data = await res.json();
        setTestimonials(data);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      company: '',
      image: '',
      rating: 5,
      text: '',
      is_active: true
    });
  };

  const handleAdd = async () => {
    if (!formData.name || !formData.text || !formData.position || !formData.company) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        setTestimonials([...testimonials, data]);
        resetForm();
        setIsAdding(false);
        toast.success('Testimonial added successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add testimonial');
      }
    } catch (error) {
      console.error('Error adding testimonial:', error);
      toast.error('Failed to add testimonial');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/testimonials/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const updated = await res.json();
        setTestimonials(testimonials.map(t => t.id === id ? updated : t));
        setEditingId(null);
        resetForm();
        toast.success('Testimonial updated successfully');
      } else {
        toast.error('Failed to update testimonial');
      }
    } catch (error) {
      console.error('Error updating testimonial:', error);
      toast.error('Failed to update testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const res = await fetch(`${API_URL}/api/testimonials/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setTestimonials(testimonials.filter(t => t.id !== id));
        toast.success('Testimonial deleted successfully');
      } else {
        toast.error('Failed to delete testimonial');
      }
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const toggleActive = async (testimonial) => {
    try {
      const res = await fetch(`${API_URL}/api/testimonials/${testimonial.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !testimonial.is_active })
      });

      if (res.ok) {
        const updated = await res.json();
        setTestimonials(testimonials.map(t => t.id === testimonial.id ? updated : t));
        toast.success(`Testimonial ${updated.is_active ? 'activated' : 'deactivated'}`);
      }
    } catch (error) {
      console.error('Error toggling testimonial:', error);
      toast.error('Failed to update testimonial');
    }
  };

  const startEdit = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      name: testimonial.name,
      position: testimonial.position,
      company: testimonial.company,
      image: testimonial.image || '',
      rating: testimonial.rating,
      text: testimonial.text,
      is_active: testimonial.is_active
    });
  };

  const RatingSelector = ({ value, onChange }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="focus:outline-none"
        >
          <Star
            size={24}
            className={`transition-colors ${
              star <= value 
                ? 'fill-[#320547] text-[#320547]' 
                : 'text-gray-300 hover:text-gray-400'
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#320547]">Client Testimonials</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage testimonials displayed on the homepage
          </p>
        </div>
        <Button
          onClick={() => { setIsAdding(true); resetForm(); }}
          className="bg-[#320547] hover:bg-[#95228A] text-white"
          disabled={isAdding}
        >
          <Plus size={16} className="mr-2" />
          Add Testimonial
        </Button>
      </div>

      {/* Add New Form */}
      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="border-[#320547]/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-[#320547]">Add New Testimonial</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                    <Input
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position *</label>
                    <Input
                      placeholder="CTO, Tech Company"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                    <Input
                      placeholder="Company Name"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Profile Image URL</label>
                    <Input
                      placeholder="https://example.com/photo.jpg"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rating *</label>
                  <RatingSelector 
                    value={formData.rating} 
                    onChange={(rating) => setFormData({ ...formData, rating })} 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Testimonial Text *</label>
                  <Textarea
                    placeholder="Write the client's testimonial here..."
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    rows={4}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={handleAdd} className="bg-[#320547] hover:bg-[#95228A]">
                    <Check size={16} className="mr-2" />
                    Add Testimonial
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setIsAdding(false); resetForm(); }}
                  >
                    <X size={16} className="mr-2" />
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Testimonials List */}
      <div className="space-y-3">
        {testimonials.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Quote className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No testimonials yet</h3>
              <p className="text-gray-500 mb-4">
                Add your first client testimonial to showcase on the homepage.
              </p>
              <Button
                onClick={() => setIsAdding(true)}
                className="bg-[#320547] hover:bg-[#95228A]"
              >
                <Plus size={16} className="mr-2" />
                Add First Testimonial
              </Button>
            </CardContent>
          </Card>
        ) : (
          testimonials.map((testimonial) => (
            <motion.div key={testimonial.id} layout>
              <Card className={`${!testimonial.is_active ? 'opacity-60 border-dashed' : ''}`}>
                <CardContent className="py-4">
                  {editingId === testimonial.id ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                          <Input
                            value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                          <Input
                            value={formData.company}
                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                          <Input
                            value={formData.image}
                            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                        <RatingSelector 
                          value={formData.rating} 
                          onChange={(rating) => setFormData({ ...formData, rating })} 
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Testimonial Text</label>
                        <Textarea
                          value={formData.text}
                          onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                          rows={4}
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(testimonial.id)}
                          className="bg-[#320547] hover:bg-[#95228A]"
                        >
                          <Check size={14} className="mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditingId(null); resetForm(); }}
                        >
                          <X size={14} className="mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex items-start gap-4">
                      {/* Client Image */}
                      <div className="flex-shrink-0">
                        {testimonial.image ? (
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-14 h-14 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-[#320547]/10 flex items-center justify-center">
                            <User size={24} className="text-[#320547]" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-gray-900">{testimonial.name}</h3>
                          {!testimonial.is_active && (
                            <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                          <span>{testimonial.position}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Building size={12} />
                            {testimonial.company}
                          </span>
                        </div>
                        <div className="flex mb-2">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} size={14} className="fill-[#320547] text-[#320547]" />
                          ))}
                          {[...Array(5 - testimonial.rating)].map((_, i) => (
                            <Star key={i} size={14} className="text-gray-300" />
                          ))}
                        </div>
                        <p className="text-gray-600 text-sm italic line-clamp-2">"{testimonial.text}"</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleActive(testimonial)}
                          className={testimonial.is_active ? "text-green-500" : "text-gray-400"}
                          title={testimonial.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <Check size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(testimonial)}
                          className="text-gray-500 hover:text-[#320547]"
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(testimonial.id)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Stats */}
      {testimonials.length > 0 && (
        <div className="flex gap-4 text-sm text-gray-500">
          <span>{testimonials.length} total testimonials</span>
          <span>{testimonials.filter(t => t.is_active).length} active</span>
          <span>{testimonials.filter(t => !t.is_active).length} inactive</span>
        </div>
      )}
    </div>
  );
};

export default TestimonialsPanel;
